import {
  TourneyFill24Icon,
  TourneyOutline24Icon,
} from "@vygruppen/spor-icon-react";
import {
  Leg,
  legsAreEqual,
  Stop,
  stopsAreSameStop,
} from "@vygruppen/vy-train-map";
import {
  InfrastructureForm,
  RedirectStretchSchema,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import {
  ALTERNATIVE_STRETCHES,
  Stretch as AlternativeStretch,
  flipLegDirections,
  formatStretchName,
  orderLegs,
  shouldFlipStretchDirection,
} from "features/CenterContent/shared/operationalInformation/alternativeStretches";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FilterChip,
  DropsBadge,
  DropsStaticAlert,
  Flex,
  Stack,
  Text,
} from "shared/components/Spor";
import { unique } from "shared/utils/arrayUtils";
/**
 * FIXME:
 * Alternative stretches for Lysaker-Asker (id: 8 and 9) that are needed for TrainChangedRoute,
 * but is irrelevant in this component.
 * TrainChangedRoute should be rewritten to use the same logic for combining redirect stretches as
 * we do here for infrastructure events.
 */
const alwaysIgnoredAlternativeStretchIds = [8, 9];

const findAlternativeStretchesMatchingLegs = (legs: Leg[]) =>
  ALTERNATIVE_STRETCHES.filter(
    (alternativeStretch) =>
      alternativeStretch.legs.every((alternativeLeg) =>
        legs.some((leg) => legsAreEqual(leg, alternativeLeg)),
      ) && !alwaysIgnoredAlternativeStretchIds.includes(alternativeStretch.id),
  );

const isRelevantAlternativeStretch = (
  alternativeStretch: AlternativeStretch,
  selectedStops: Stop[],
  affectedAlternativeStretches: AlternativeStretch[],
) => {
  const ignoredAlternativeStretchIds = affectedAlternativeStretches.map(
    (s) => s.id,
  );

  const shouldIgnoreStretch =
    ignoredAlternativeStretchIds.includes(alternativeStretch.id) ||
    alwaysIgnoredAlternativeStretchIds.includes(alternativeStretch.id);

  return (
    !shouldIgnoreStretch &&
    selectedStops.some((stop) =>
      stopsAreSameStop(stop, alternativeStretch.stops[0]),
    ) &&
    selectedStops.some((stop) =>
      stopsAreSameStop(stop, alternativeStretch.stops.at(-1)!),
    )
  );
};

const toRedirectStretch = (
  stretch: AlternativeStretch,
): RedirectStretchSchema => ({
  name: stretch.name,
  legs: stretch.legs,
});

const combineAndMapToRedirectStretches = (
  redirectStretches: RedirectStretchSchema[],
  currentStretchId: number,
): RedirectStretchSchema[] => {
  const currentStretch = ALTERNATIVE_STRETCHES.find(
    (s) => s.id === currentStretchId,
  );

  if (!currentStretch) {
    return redirectStretches;
  }

  const connectedStretch = redirectStretches.at(-1);

  if (connectedStretch) {
    let combinedStretchName = connectedStretch.name;

    if (connectedStretch.name !== currentStretch.name) {
      combinedStretchName += ` - ${currentStretch.name}`;
    }

    return [
      {
        name: combinedStretchName,
        legs: orderLegs([...connectedStretch.legs, ...currentStretch.legs]),
      },
    ];
  }

  return [...redirectStretches, toRedirectStretch(currentStretch)];
};

export const SelectRedirectStretches = () => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<InfrastructureForm>();
  const prevRedirectStretches = getValues(
    "infrastructureForm.redirectStretches",
  );
  const prevStretchName = getValues("infrastructureForm.stretchName");
  const affectedLegs = getValues("infrastructureForm.affectedLegs");

  const prevAlternativeStretchIds = prevRedirectStretches
    .flatMap((prev) => findAlternativeStretchesMatchingLegs(prev.legs))
    .map((s) => s.id);

  const [selectedAlternativeStretches, setSelectedAlternativeStretches] =
    useState<number[]>(prevAlternativeStretchIds);

  const affectedAlternativeStretches =
    findAlternativeStretchesMatchingLegs(affectedLegs);

  const selectedStops = unique(
    affectedLegs.flatMap((leg) => [leg.fromStop, leg.toStop]),
  );

  const relevantAlternativeStretches = ALTERNATIVE_STRETCHES.filter((stretch) =>
    isRelevantAlternativeStretch(
      stretch,
      selectedStops,
      affectedAlternativeStretches,
    ),
  );

  useEffect(() => {
    const selectedRedirectStretchesCombined = selectedAlternativeStretches
      .reduce(combineAndMapToRedirectStretches, [])
      .map((s) => {
        const shouldFlip = shouldFlipStretchDirection(s.legs, selectedStops);
        return {
          ...s,
          legs: shouldFlip ? flipLegDirections(s.legs) : s.legs,
        };
      });

    setValue(
      "infrastructureForm.redirectStretches",
      selectedRedirectStretchesCombined,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );

    const allEqualAffectedStretchNames = affectedAlternativeStretches.every(
      (s) => s.name === affectedAlternativeStretches[0].name,
    );

    // If all affected stretch sections have the same name, append the name of the affected stretch to stretchName.
    const stretchName =
      selectedRedirectStretchesCombined.length > 0 &&
      allEqualAffectedStretchNames
        ? `${prevStretchName.split("via")[0].trim()} ${t("via")} ${affectedAlternativeStretches[0].name}`
        : `${prevStretchName.split("via")[0].trim()}`;

    setValue("infrastructureForm.stretchName", stretchName, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [selectedAlternativeStretches]);

  return (
    <Stack mb="12px">
      <Text variant="xs">{t("Velg omkjøringer:")}</Text>
      <Flex alignItems="start" flexWrap="wrap" gap="6px">
        {relevantAlternativeStretches.length === 0 ? (
          <DropsBadge semantics="warning">
            {t("Ingen omkjøringer finnes")}
          </DropsBadge>
        ) : (
          relevantAlternativeStretches.map((stretch) => (
            <FilterChip
              key={stretch.id}
              icon={{
                default: <TourneyOutline24Icon />,
                checked: <TourneyFill24Icon />,
              }}
              variant="core"
              fontWeight="700"
              size="sm"
              checked={selectedAlternativeStretches.includes(stretch.id)}
              onCheckedChange={() => {
                if (!selectedAlternativeStretches.includes(stretch.id)) {
                  setSelectedAlternativeStretches((prev) =>
                    prev ? [...prev, stretch.id ?? []] : [stretch.id],
                  );
                } else {
                  setSelectedAlternativeStretches((prev) =>
                    prev ? prev.filter((id) => id !== stretch.id) : [],
                  );
                }
              }}
            >
              {formatStretchName(
                stretch,
                shouldFlipStretchDirection(stretch.legs, selectedStops),
              )}
            </FilterChip>
          ))
        )}
        {selectedAlternativeStretches.length > 0 && (
          <DropsStaticAlert variant="important">
            {t("Kontrollér opprinnelig strekning i strekningsnavn")}
          </DropsStaticAlert>
        )}
      </Flex>
    </Stack>
  );
};
